import {
  DataFieldWithDataType,
  toDataFieldWithDataType,
} from '../../../../../common-types';
import {
  ApiMasterDataAdvancedQuery,
  ApiMasterDataAdvancedQueryDimension,
  ApiMasterDataQueryMeasure,
  ApiMasterDataQueryResponse,
  JoinTypes,
} from '../../../../common/api/api-interfaces';
import { CachedMasterDataService } from '../../../../common/api/cached-master-data-service';
import { UndefinedValue } from '../../../../common/constants/systemValuesTranslation';
import { dateManagerService } from '../../../../common/date-manager/date-manager-service';
import {
  getVersionFilter,
  getVersionsFilter,
} from '../../../../common/filter/common-filters';
import { TimeSliderConfig } from '../../../../common/filter/filter-store';
import {
  combineFilters,
  getChartColorsForIndex,
  getVersionFilterFromDate,
} from '../../../../common/filter/utils';
import {
  ByDimensionService,
  CustomDataPointTransformer,
} from '../../../../common/services/ByDimensionService';
import { GetResponseForAdvancedQueryService } from '../../../../common/services/GetResponseForQueryService';
import { DataMapAndLabels, Service } from '../../../../common/services/utils';
import {
  getValue,
  getValueForMeasure,
  getValueOr,
  percentage,
  sum,
  uuid,
} from '../../../../common/utilFunctions/utils';
import { hashString, VersionId } from '../../../../common/v2/api/types';
import {
  LegacyDomainScopedStore,
  LegacyStore,
} from '../../../../common/v2/context/legacy-types';
import { getMomentDate } from '../../../../common/v2/date/date-utils';
import {
  ApiMasterDataQueryFilterItem,
  DataFields,
  DataTypes,
  EmployeeDataFields,
  Operations,
  SurveyDataFields,
} from '../../../../common/v2/types';
import { Cycle } from '../survey-store';
import { SurveyMetrics } from './../survey-store';
import {
  AllQuestionsWithSentimentInputs,
  AnswerTypes,
  OverallQuestionByDimensionServiceInputs,
  OverallQuestionTotalServiceInputs,
  QuestionAvgScoreInputs,
  QuestionServiceInputs,
  QuestionTypes,
  Sentiments,
  SurveyQuestionResponse,
  SurveyQuestionResult,
  SurveySentimentMap,
  SurveySentimentResponse,
  SurveyService,
} from './SurveyService';

export interface MultiCycleRespondentsByDimensionDataMap {
  [cycle: string]: Record<string, Record<string, number>>;
}
export class LiveSurveyService implements SurveyService {
  public overallQuestionTotal: Service<
    OverallQuestionTotalServiceInputs,
    DataMapAndLabels
  > = (async (
    inputs,
    filters,
    latestVersions,
    legacyStores,
    legacyDomainScopedStores
  ) => {
    const latestVersion = latestVersions[DataTypes.EMPLOYEE];

    if (!latestVersion) {
      throw new Error(`Latest version for datatype employee is undefined`);
    }
    const { parseApiDate } = dateManagerService.getInstance();
    const {
      selectedCycles,
      selectedOverallQuestion,
      selectedMetricValue,
      timeSliderConfig,
      temporalityFilter,
    } = inputs;
    const { endDate, firstMonthOfYear } = timeSliderConfig;
    const allFilters = combineFilters([
      this.cycleFilter(selectedCycles),
      this.questionFilter([selectedOverallQuestion]),
      temporalityFilter,
      getVersionFilterFromDate(
        parseApiDate(endDate),
        firstMonthOfYear,
        latestVersion,
        DataTypes.EMPLOYEE
      ),
      ...this.getEmployeeFilterForCycle(timeSliderConfig, filters),
    ]);
    const measures = [this.avgScoreMeasure, this.numberOfRespondentsMeasure];
    const query: ApiMasterDataAdvancedQuery = {
      dataType: DataTypes.QUESTIONANSWER,
      dimensions: [
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.CYCLE,
        },
      ],
      measures,
      joins: [this.questionAnswerEmployeeJoin],
      filterItems: allFilters,
    };
    const response = await GetResponseForAdvancedQueryService(
      query,
      this.cachedMasterDataApiService
    );
    const dataMap =
      response.dataPoints?.reduce((acc, dp) => {
        const cycleLabel: string = getValueOr(dp.dimensions, {
          dataType: DataTypes.QUESTIONANSWER,
          dataField: SurveyDataFields.CYCLE,
        });
        const measure = getValueForMeasure(
          dp.measures,
          selectedMetricValue === SurveyMetrics.AVG_SCORE
            ? {
                dataType: DataTypes.QUESTIONANSWER,
                dataField: SurveyDataFields.SCORE,
              }
            : {
                dataType: DataTypes.QUESTIONANSWER,
                dataField: SurveyDataFields.EMPLOYEE_ID,
              }
        );
        return {
          ...acc,
          [cycleLabel]: { [selectedMetricValue]: measure },
        };
      }, {}) ?? {};
    return { dataMap, labels: Object.keys(dataMap) };
  }) as Service<OverallQuestionTotalServiceInputs, DataMapAndLabels>;

  public overallQuestionByDimensionAvgScores: Service<
    OverallQuestionByDimensionServiceInputs,
    DataMapAndLabels
  > = (async (
    inputs,
    filters,
    latestVersions,
    legacyStores,
    legacyDomainScopedStores
  ) => {
    const latestVersion = latestVersions[DataTypes.EMPLOYEE];

    if (!latestVersion) {
      throw new Error(`Latest version for datatype employee is undefined`);
    }
    const { parseApiDate } = dateManagerService.getInstance();
    const {
      selectedCycles,
      selectedOverallQuestion,
      timeSliderConfig,
      temporalityFilter,
    } = inputs;
    const { endDate, firstMonthOfYear } = timeSliderConfig;
    const allFilters = combineFilters([
      this.cycleFilter(selectedCycles),
      this.questionFilter([selectedOverallQuestion]),
      temporalityFilter,
      getVersionFilterFromDate(
        parseApiDate(endDate),
        firstMonthOfYear,
        latestVersion,
        DataTypes.EMPLOYEE
      ),
      ...this.getEmployeeFilterForCycle(timeSliderConfig, filters),
    ]);
    const getDataMapValueFromDataPoints: CustomDataPointTransformer = (
      dataPoints
    ) => {
      return dataPoints.reduce((acc, dp) => {
        const cycleLabel: string = getValueOr(dp.dimensions, {
          dataType: DataTypes.QUESTIONANSWER,
          dataField: SurveyDataFields.CYCLE,
        });
        const measure = getValueForMeasure(dp.measures, {
          dataType: DataTypes.QUESTIONANSWER,
          dataField: SurveyDataFields.SCORE,
        });
        return {
          ...acc,
          [cycleLabel]: { [SurveyMetrics.AVG_SCORE]: measure },
        };
      }, {});
    };

    const baseQuery: ApiMasterDataAdvancedQuery = {
      dataType: DataTypes.QUESTIONANSWER,
      dimensions: [
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.CYCLE,
        },
      ],
      measures: [this.avgScoreMeasure],
      joins: [this.questionAnswerEmployeeJoin],
    };

    const { dataMap, labels } = await ByDimensionService(
      {
        dimension: inputs.dimension,
        filters: allFilters,
        baseQuery,
        dataKey: SurveyMetrics.AVG_SCORE,
        dataType: DataTypes.QUESTIONANSWER,
        getDataMapValueFromDataPoints,
        useAdvancedQuery: true,
      },
      latestVersions,
      legacyStores,
      legacyDomainScopedStores
    );
    return { dataMap, labels };
  }) as Service<OverallQuestionByDimensionServiceInputs, DataMapAndLabels>;

  public overallQuestionByDimensionRespondents: Service<
    OverallQuestionByDimensionServiceInputs,
    DataMapAndLabels
  > = (async (
    inputs,
    filters,
    latestVersions,
    legacyStores,
    legacyDomainScopedStores
  ) => {
    const latestVersion = latestVersions[DataTypes.EMPLOYEE];
    if (!latestVersion) {
      throw new Error(`Latest version for datatype employee is undefined`);
    }
    const { parseApiDate } = dateManagerService.getInstance();
    const {
      selectedCycles,
      selectedOverallQuestion,
      timeSliderConfig,
      temporalityFilter,
    } = inputs;
    const { endDate, firstMonthOfYear } = timeSliderConfig;
    const allFilters = combineFilters([
      this.cycleFilter(selectedCycles),
      this.questionFilter([selectedOverallQuestion]),
      temporalityFilter,
      getVersionFilterFromDate(
        parseApiDate(endDate),
        firstMonthOfYear,
        latestVersion,
        DataTypes.EMPLOYEE
      ),
      ...this.getEmployeeFilterForCycle(timeSliderConfig, filters),
    ]);
    const getDataMapValueFromDataPoints: CustomDataPointTransformer = (
      dataPoints
    ) => {
      return dataPoints.reduce(
        (acc, dp) => {
          const cycleLabel: string = getValueOr(dp.dimensions, {
            dataType: DataTypes.QUESTIONANSWER,
            dataField: SurveyDataFields.CYCLE,
          });
          const score: string = getValueOr(dp.dimensions, {
            dataType: DataTypes.QUESTIONANSWER,
            dataField: SurveyDataFields.SCORE,
          });
          const measure = getValueForMeasure(dp.measures, {
            dataType: DataTypes.QUESTIONANSWER,
            dataField: SurveyDataFields.EMPLOYEE_ID,
          });
          return {
            ...acc,
            [cycleLabel]: { ...acc[cycleLabel], [score]: measure },
          };
        },
        {} as Record<string, any>
      );
    };

    const baseQuery: ApiMasterDataAdvancedQuery = {
      dataType: DataTypes.QUESTIONANSWER,
      dimensions: [
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.CYCLE,
        },
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.SCORE,
        },
      ],
      measures: [this.numberOfRespondentsMeasure],
      joins: [this.questionAnswerEmployeeJoin],
    };

    const { dataMap, labels } = await ByDimensionService(
      {
        dimension: inputs.dimension,
        filters: allFilters,
        baseQuery,
        dataKey: SurveyMetrics.RESPONDENT_COUNT,
        dataType: DataTypes.QUESTIONANSWER,
        getDataMapValueFromDataPoints,
        useAdvancedQuery: true,
      },
      latestVersions,
      legacyStores,
      legacyDomainScopedStores
    );

    const flipCycleAndDimLabel = (
      dataMap: MultiCycleRespondentsByDimensionDataMap
    ) => {
      const newDataMap: MultiCycleRespondentsByDimensionDataMap = {};
      Object.keys(dataMap).forEach((dimLabel) => {
        Object.keys(dataMap[dimLabel]).forEach((cycle) => {
          newDataMap[cycle] = newDataMap[cycle] ?? {};
          newDataMap[cycle][dimLabel] = dataMap[dimLabel][cycle];
        });
      });
      return newDataMap;
    };
    const newDataMap = flipCycleAndDimLabel(dataMap);
    return { dataMap: newDataMap, labels };
  }) as Service<OverallQuestionByDimensionServiceInputs, DataMapAndLabels>;

  public overallQuestionAvgScore: Service<
    OverallQuestionByDimensionServiceInputs,
    DataMapAndLabels
  > = (async (
    inputs: OverallQuestionByDimensionServiceInputs,
    filters: ApiMasterDataQueryFilterItem[],
    latestVersions: Partial<Record<DataTypes, VersionId>>,
    legacyStores: LegacyStore,
    legacyDomainScopedStores: LegacyDomainScopedStore
  ): Promise<{ dataMap: Record<string, any>; labels: string[] }> => {
    return { dataMap: {}, labels: [] };
  }) as Service<OverallQuestionByDimensionServiceInputs, DataMapAndLabels>;

  public getAllQuestionsWithSentiment: Service<
    AllQuestionsWithSentimentInputs,
    SurveySentimentResponse
  > = (async (
    inputs,
    filters,
    latestVersions,
    legacyStores,
    legacyDomainScopedStores
  ) => {
    const latestVersion = latestVersions[DataTypes.EMPLOYEE];
    if (!latestVersion) {
      throw new Error(`Latest version for datatype employee is undefined`);
    }
    const { timeSliderStore } = legacyDomainScopedStores;
    const { timeSliderConfig } = timeSliderStore;
    const { endDate, firstMonthOfYear } = timeSliderConfig;
    const { parseApiDate } = dateManagerService.getInstance();
    const query: ApiMasterDataAdvancedQuery = this.surveyQuery(
      [
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.QUESTION,
        },
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.SENTIMENT,
        },
      ],
      combineFilters([
        this.cycleFilter([inputs.selectedCycle]),
        ...this.getEmployeeFilterForCycle(timeSliderConfig, filters),
        inputs.temporalityFilter,
        getVersionFilterFromDate(
          parseApiDate(endDate),
          firstMonthOfYear,
          latestVersion,
          DataTypes.EMPLOYEE
        ),
      ]),
      [
        {
          dataType: DataTypes.QUESTIONANSWER,
          operation: Operations.COUNT,
          property: SurveyDataFields.QUESTION,
        },
      ]
    );

    const response = await GetResponseForAdvancedQueryService(
      query,
      this.cachedMasterDataApiService
    );
    const result = this.groupByQuestion(response);
    return this.addPercentageForEachSentiment(result);
  }) as Service<AllQuestionsWithSentimentInputs, SurveySentimentResponse>;

  public questionByDimension: Service<QuestionServiceInputs, DataMapAndLabels> =
    (async (
      inputs: QuestionServiceInputs,
      filters: ApiMasterDataQueryFilterItem[],
      latestVersions: Partial<Record<DataTypes, VersionId>>,
      legacyStores: LegacyStore,
      legacyDomainScopedStores: LegacyDomainScopedStore
    ) => {
      const { selectedCycles, selectedQuestions, selectedTheme } = inputs;
      const latestEmployeeVersion = latestVersions[DataTypes.EMPLOYEE];

      const allFilters = [
        this.cycleFilter(selectedCycles),
        ...(selectedTheme === 'All'
          ? []
          : [this.questionFilter(selectedQuestions)]),
        ...(filters ?? []),
        this.getVersionFilterForCycle(selectedCycles, latestEmployeeVersion),
        this.getVersionFilter(selectedCycles.map((c) => c.endDate)),
      ];
      const getDataMapValueFromDataPoints: CustomDataPointTransformer = (
        dataPoints
      ) => {
        return dataPoints.reduce((acc, dp) => {
          const cycleLabel: string = getValueOr(dp.dimensions, {
            dataType: DataTypes.QUESTIONANSWER,
            dataField: SurveyDataFields.CYCLE,
          });
          const measure = getValueForMeasure(dp.measures, {
            dataType: DataTypes.QUESTIONANSWER,
            dataField: SurveyDataFields.SCORE,
          });
          return {
            ...acc,
            [cycleLabel]: measure,
          };
        }, {});
      };
      const baseQueryByCycle: ApiMasterDataAdvancedQuery = {
        dimensions: [
          {
            dataType: DataTypes.QUESTIONANSWER,
            property: SurveyDataFields.CYCLE,
          },
        ],
        dataType: DataTypes.QUESTIONANSWER,
        measures: [this.avgScoreMeasure],
      };
      const { dataMap, labels } = await ByDimensionService(
        {
          dimension: inputs.dimension,
          filters: allFilters,
          getDataMapValueFromDataPoints,
          dataType: DataTypes.EMPLOYEE,
          baseQuery: baseQueryByCycle,
          dataKey: '',
          useAdvancedQuery: true,
        },
        latestVersions,
        legacyStores,
        legacyDomainScopedStores
      );
      return { dataMap, labels };
    }) as Service<QuestionServiceInputs, DataMapAndLabels>;

  public questionAvgScore: Service<
    QuestionAvgScoreInputs,
    SurveyQuestionResponse
  > = (async (
    inputs: QuestionAvgScoreInputs,
    filters: ApiMasterDataQueryFilterItem[],
    latestVersions: Partial<Record<DataTypes, VersionId>>,
    legacyStores: LegacyStore,
    legacyDomainScopedStores: LegacyDomainScopedStore
  ) => {
    const {
      selectedMetricValue,
      selectedCycles,
      temporalityFilter,
      timeSliderConfig,
    } = inputs;
    const allFilters = combineFilters([
      this.cycleFilter(selectedCycles),
      ...this.getEmployeeFilterForCycle(timeSliderConfig, filters),
      this.getVersionFilter(selectedCycles.map((c) => c.endDate)),
      temporalityFilter,
    ]);
    const measures = [this.avgScoreMeasure, this.numberOfRespondentsMeasure];
    const query = this.surveyQuery(
      [
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.CYCLE,
        },
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.THEME_NAME,
        },
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.QUESTION,
        },
      ],
      allFilters,
      measures
    );
    const response = await GetResponseForAdvancedQueryService(
      query,
      this.cachedMasterDataApiService
    );
    const result: SurveyQuestionResult =
      response.dataPoints?.reduce<SurveyQuestionResult>((acc, dp) => {
        const cycleLabel: string = getValue(dp.dimensions, {
          dataType: DataTypes.QUESTIONANSWER,
          dataField: SurveyDataFields.CYCLE,
        });
        const theme: string = getValue(dp.dimensions, {
          dataType: DataTypes.QUESTIONANSWER,
          dataField: SurveyDataFields.THEME_NAME,
        });
        const question: string = getValue(dp.dimensions, {
          dataType: DataTypes.QUESTIONANSWER,
          dataField: SurveyDataFields.QUESTION,
        });
        const measure = getValueForMeasure(dp.measures, {
          dataType: DataTypes.QUESTIONANSWER,
          dataField:
            selectedMetricValue === SurveyMetrics.AVG_SCORE
              ? SurveyDataFields.SCORE
              : SurveyDataFields.EMPLOYEE_ID,
        });
        if (theme && question) {
          acc[cycleLabel] = acc[cycleLabel] || {};
          acc[cycleLabel][theme] = acc[cycleLabel][theme] || {};
          acc[cycleLabel][theme][question] = Number(measure);
        }
        return acc;
      }, {} as SurveyQuestionResult) ?? {};

    const queryPerTheme = this.surveyQuery(
      [
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.CYCLE,
        },
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.THEME_NAME,
        },
      ],
      allFilters,
      measures
    );

    const responsePerTheme = await GetResponseForAdvancedQueryService(
      queryPerTheme,
      this.cachedMasterDataApiService
    );
    responsePerTheme.dataPoints?.forEach((dp) => {
      const cycleLabel: string = getValue(dp.dimensions, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField: SurveyDataFields.CYCLE,
      });
      const theme: string = getValue(dp.dimensions, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField: SurveyDataFields.THEME_NAME,
      });
      const measure = getValueForMeasure(dp.measures, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField:
          selectedMetricValue === SurveyMetrics.AVG_SCORE
            ? SurveyDataFields.SCORE
            : SurveyDataFields.EMPLOYEE_ID,
      });
      if (theme) {
        result[cycleLabel][theme]['all'] = Number(measure);
      }
    });
    return { response: result, selectedCycles, selectedMetricValue };
  }) as Service<QuestionAvgScoreInputs, SurveyQuestionResponse>;

  private numberOfRespondentsMeasure: ApiMasterDataQueryMeasure = {
    dataType: DataTypes.QUESTIONANSWER,
    operation: Operations.COUNT_DISTINCT,
    property: SurveyDataFields.EMPLOYEE_ID,
  };

  private avgScoreMeasure: ApiMasterDataQueryMeasure = {
    dataType: DataTypes.QUESTIONANSWER,
    operation: Operations.AVG,
    property: SurveyDataFields.SCORE,
  };

  private distinctCyclesMeasure: ApiMasterDataQueryMeasure = {
    dataType: DataTypes.QUESTIONANSWER,
    operation: Operations.COUNT_DISTINCT,
    property: SurveyDataFields.CYCLE,
  };

  private questionAnswerEmployeeJoin = {
    joinType: JoinTypes.INNER,
    joinDataType: DataTypes.EMPLOYEE,
    joinConditions: [
      {
        leftDataType: DataTypes.QUESTIONANSWER,
        leftProperty: SurveyDataFields.EMPLOYEE_ID,
        rightDataType: DataTypes.EMPLOYEE,
        rightProperty: EmployeeDataFields.EMPLOYEE_ID,
        operation: Operations.EQUAL,
      },
    ],
  };

  constructor(readonly cachedMasterDataApiService: CachedMasterDataService) {
    this.overallQuestionByDimensionAvgScores.cacheKey = uuid();
    this.overallQuestionAvgScore.cacheKey = uuid();
    this.getAllQuestionsWithSentiment.cacheKey = uuid();
    this.questionByDimension.cacheKey = uuid();
    this.questionAvgScore.cacheKey = uuid();
  }

  public getDeps = () => [
    hashString([...this.cachedMasterDataApiService.getDeps()]),
  ];

  public questionsReportQuery: (
    selectedCycles: Cycle[],
    fields: DataFieldWithDataType[],
    filters: ApiMasterDataQueryFilterItem[],
    selectedQuestions: string[],
    latestEmployeeVersion: string,
    timeSliderConfig: TimeSliderConfig
  ) => ApiMasterDataAdvancedQuery = (
    selectedCycles: Cycle[],
    fields: DataFieldWithDataType[],
    filters: ApiMasterDataQueryFilterItem[],
    selectedQuestions: string[],
    latestEmployeeVersion: string,
    timeSliderConfig: TimeSliderConfig
  ) => {
    return {
      dataType: DataTypes.QUESTIONANSWER,
      dimensions: fields.map((f) => ({
        dataType: f.dataType,
        property: f.dataField,
      })),
      filterItems: combineFilters([
        this.cycleFilter(selectedCycles),
        ...(selectedQuestions.length > 0
          ? [this.questionFilter(selectedQuestions)]
          : []),
        this.getVersionFilter(selectedCycles.map((c) => c.endDate)),
        ...this.getEmployeeFilterForCycle(timeSliderConfig, filters),
      ]),
      joins: [
        {
          ...this.questionAnswerEmployeeJoin,
          joinConditions: [
            ...this.questionAnswerEmployeeJoin.joinConditions,
            {
              leftDataType: DataTypes.QUESTIONANSWER,
              leftProperty: SurveyDataFields.VERSION_ID,
              rightDataType: DataTypes.EMPLOYEE,
              rightProperty: EmployeeDataFields.VERSION_ID,
              operation: Operations.EQUAL,
            },
          ],
        },
      ],
    };
  };
  // TODO: Even though I set the cacheKey property in constructor, Typescript doesn't see it for some reason.
  // To avoid that error, unfortunately I am having to explicitly cast the service to the Service type.
  // Should figure out a better solution later.

  public getAllCycles = async (
    questionTypes?: QuestionTypes[],
    answerTypes?: AnswerTypes[]
  ): Promise<Cycle[]> => {
    const allCyclesQuery: ApiMasterDataAdvancedQuery = this.surveyQuery(
      [
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.CYCLE,
        },
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.CYCLE_START_DATE,
        },
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.CYCLE_END_DATE,
        },
        {
          dataType: DataTypes.QUESTIONANSWER,
          property: SurveyDataFields.VERSION_ID,
        },
      ],
      [
        ...(questionTypes ? [this.questionTypeFilter(questionTypes)] : []),
        ...(answerTypes ? [this.answerTypeFilter(answerTypes)] : []),
      ],
      [this.distinctCyclesMeasure]
    );
    const response = await GetResponseForAdvancedQueryService(
      allCyclesQuery,
      this.cachedMasterDataApiService
    );
    const c = response?.dataPoints?.map((dp, i) => ({
      label: getValueOr(dp.dimensions, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField: SurveyDataFields.CYCLE,
      }),
      versionId: getValueOr(dp.dimensions, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField: SurveyDataFields.VERSION_ID,
      }),
      startDate: getValueOr(dp.dimensions, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField: SurveyDataFields.CYCLE_START_DATE,
      }),
      endDate: getValueOr(dp.dimensions, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField: SurveyDataFields.CYCLE_END_DATE,
      }),
      color: getChartColorsForIndex(i),
    }));
    return c;
  };

  public getAllScores = async () => {
    const scoreDim = {
      dataType: DataTypes.QUESTIONANSWER,
      property: SurveyDataFields.SCORE,
    };
    const allScoresQuery: ApiMasterDataAdvancedQuery = this.surveyQuery([
      scoreDim,
    ]);
    const response = await GetResponseForAdvancedQueryService(
      allScoresQuery,
      this.cachedMasterDataApiService
    );
    const scores = response.dataPoints.map(
      (dp) =>
        getValueOr(
          dp.dimensions,
          toDataFieldWithDataType(scoreDim.dataType, scoreDim.property)
        ) as string
    );
    return scores;
  };

  public getAllQuestions = async (
    cycles: Cycle[],
    questionTypes: QuestionTypes[] = [
      QuestionTypes.OVERALL,
      QuestionTypes.SPECIFIC,
    ],
    answerTypes: AnswerTypes[] = [AnswerTypes.ORDINAL, AnswerTypes.NOMINAL]
  ): Promise<string[]> => {
    const allQuestionsByThemeQuery: ApiMasterDataAdvancedQuery =
      this.surveyQuery(
        [
          {
            dataType: DataTypes.QUESTIONANSWER,
            property: SurveyDataFields.QUESTION,
          },
        ],
        [
          this.questionTypeFilter(questionTypes),
          this.answerTypeFilter(answerTypes),
          this.cycleFilter(cycles),
          this.getVersionFilter(cycles.map((c) => c.endDate)),
        ]
      );
    const response = await GetResponseForAdvancedQueryService(
      allQuestionsByThemeQuery,
      this.cachedMasterDataApiService
    );
    return response.dataPoints.flatMap((dp) =>
      getValue(dp.dimensions, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField: SurveyDataFields.QUESTION,
      })
    );
  };

  private getEmployeeFilterForCycle = (
    timeSliderConfig: TimeSliderConfig,
    filters?: ApiMasterDataQueryFilterItem[]
  ) =>
    filters?.length
      ? [...filters, getVersionFilter(timeSliderConfig.endDate)]
      : [];

  private questionAnswerFilter: (
    values: string[],
    field: DataFields,
    op?: Operations
  ) => ApiMasterDataQueryFilterItem = (
    values: string[],
    field: DataFields,
    op: Operations = Operations.EQUAL
  ) => ({
    dataType: DataTypes.QUESTIONANSWER,
    operation: op,
    property: field,
    values,
  });

  private cycleFilter: (values: Cycle[]) => ApiMasterDataQueryFilterItem = (
    values: Cycle[]
  ) =>
    this.questionAnswerFilter(
      values.map((v) => v.label),
      SurveyDataFields.CYCLE
    );

  private questionFilter: (values: string[]) => ApiMasterDataQueryFilterItem = (
    values: string[]
  ) => this.questionAnswerFilter(values, SurveyDataFields.QUESTION);

  private questionTypeFilter: (
    values: QuestionTypes[]
  ) => ApiMasterDataQueryFilterItem = (values: QuestionTypes[]) =>
    this.questionAnswerFilter(values, SurveyDataFields.QUESTION_TYPE);

  private answerTypeFilter: (
    values: AnswerTypes[]
  ) => ApiMasterDataQueryFilterItem = (values: AnswerTypes[]) =>
    this.questionAnswerFilter(values, SurveyDataFields.ANSWER_TYPE);

  private getVersionFilter: (dates: string[]) => ApiMasterDataQueryFilterItem =
    (dates: string[]) =>
      getVersionsFilter(dates, Operations.EQUAL, DataTypes.QUESTIONANSWER);

  private getVersionFilterForCycle = (
    selectedCycles: Cycle[],
    latestVersionId: string | undefined
  ) =>
    getVersionsFilter(
      selectedCycles.map((cycle) =>
        latestVersionId &&
        getMomentDate(latestVersionId).isBefore(getMomentDate(cycle.endDate))
          ? latestVersionId
          : cycle.endDate
      )
    );

  private groupByQuestion = (response: ApiMasterDataQueryResponse) => {
    return response?.dataPoints?.reduce<SurveySentimentResponse>((acc, dp) => {
      const question = getValueOr(dp.dimensions, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField: SurveyDataFields.QUESTION,
      });
      const sentiment = getValueOr(dp.dimensions, {
        dataType: DataTypes.QUESTIONANSWER,
        dataField: SurveyDataFields.SENTIMENT,
      });
      const count = Number(
        getValueForMeasure(dp.measures, {
          dataType: DataTypes.QUESTIONANSWER,
          dataField: SurveyDataFields.QUESTION,
        }) ?? UndefinedValue
      );
      return {
        ...acc,
        [question]: { ...(acc[question] ?? {}), [sentiment]: { count } },
      };
    }, {});
  };

  private addPercentageForEachSentiment = (result: SurveySentimentResponse) => {
    return Object.entries(result ?? {}).reduce(
      (acc, [question, sentimentMap]) => {
        const fullSentimentMap = this.addMissingSentiments(sentimentMap);
        const total = sum(Object.values(fullSentimentMap).map((e) => e.count));
        return {
          ...acc,
          [question]: Object.entries(fullSentimentMap).reduce(
            (acc2, [sentiment, v]) => ({
              ...acc2,
              [sentiment]: {
                count: v.count,
                percentage: percentage(v.count, total),
              },
            }),
            {}
          ),
        };
      },
      {}
    );
  };

  private addMissingSentiments: (
    sentimentMap: SurveySentimentMap
  ) => SurveySentimentMap = (sentimentMap: SurveySentimentMap) => {
    return Object.values(Sentiments).reduce<SurveySentimentMap>(
      (acc, sentiment) => {
        return sentimentMap[sentiment]
          ? { ...acc, [sentiment]: sentimentMap[sentiment] }
          : acc;
      },
      {
        [Sentiments.POSITIVE]: { count: 0, percentage: 0 },
        [Sentiments.NEUTRAL]: { count: 0, percentage: 0 },
        [Sentiments.NEGATIVE]: { count: 0, percentage: 0 },
        [Sentiments.NA]: { count: 0, percentage: 0 },
      }
    );
  };

  private surveyQuery = (
    dimensions?: ApiMasterDataAdvancedQueryDimension[],
    filterItems?: ApiMasterDataQueryFilterItem[],
    measures?: ApiMasterDataQueryMeasure[]
  ) => ({
    dataType: DataTypes.QUESTIONANSWER,
    filterItems,
    measures,
    dimensions,
    joins:
      dimensions?.find((d) => d.dataType === DataTypes.EMPLOYEE) ||
      filterItems?.find((fi) => fi.dataType === DataTypes.EMPLOYEE) ||
      measures?.find((m) => m.dataType === DataTypes.EMPLOYEE)
        ? [this.questionAnswerEmployeeJoin]
        : [],
  });
}
